/**
 * High-level offline documents API.
 * Provides typed access to stored documents using Types.Document.
 */

import * as Types from '@aeppic/types';
import { DocumentsStoreAsync, type StoredDocument } from './stores-async.js';

/**
 * Query options for finding documents.
 */
export interface DocumentQueryOptions {
    /** Include all versions, not just the latest */
    includeAllVersions?: boolean;
    /** Limit results */
    limit?: number;
    /** Offset for pagination */
    offset?: number;
}

/**
 * High-level API for offline document storage.
 * Works with Types.Document objects directly.
 */
export class OfflineDocuments {
    constructor(private store: DocumentsStoreAsync) {}

    // ========================================================================
    // Write Operations
    // ========================================================================

    /**
     * Save a document to offline storage.
     * 
     * @param document - The document to save
     * @returns Promise that resolves when saved
     * 
     * @example
     * ```typescript
     * await offline.Documents.save(myDocument);
     * ```
     */
    async save(document: Types.Document): Promise<void> {
        const json = JSON.stringify(document);
        await this.store.save({
            id: document.id,
            v: this._versionToNumber(document.v),
            p: document.p,
            f_id: document.f?.id ?? null,
            f_v: document.f?.v ? this._versionToNumber(document.f.v) : null,
            name: (document as any).name ?? null,
            json
        });
    }

    /**
     * Save multiple documents in a batch.
     * 
     * @param documents - Array of documents to save
     * @returns Promise that resolves when all are saved
     */
    async saveMany(documents: Types.Document[]): Promise<void> {
        // TODO: Could optimize with a single transaction in the worker
        for (const doc of documents) {
            await this.save(doc);
        }
    }

    /**
     * Delete a document by ID.
     * Removes all versions of the document.
     * 
     * @param id - Document ID to delete
     */
    async delete(id: string): Promise<void> {
        await this.store.delete(id);
    }

    /**
     * Delete multiple documents by ID.
     * 
     * @param ids - Array of document IDs to delete
     */
    async deleteMany(ids: string[]): Promise<void> {
        for (const id of ids) {
            await this.store.delete(id);
        }
    }

    // ========================================================================
    // Read Operations
    // ========================================================================

    /**
     * Get a document by ID.
     * Returns the latest version if no version specified.
     * 
     * @param id - Document ID
     * @param version - Optional specific version to retrieve
     * @returns The document or null if not found
     * 
     * @example
     * ```typescript
     * const doc = await offline.Documents.get('doc-123');
     * const oldDoc = await offline.Documents.get('doc-123', 'v1');
     * ```
     */
    async get(id: string, version?: string): Promise<Types.Document | null> {
        let stored: StoredDocument | null;
        
        if (version !== undefined) {
            stored = await this.store.getVersion(id, this._versionToNumber(version));
        } else {
            stored = await this.store.getLatest(id);
        }

        if (!stored) {
            return null;
        }

        return this._parseDocument(stored);
    }

    /**
     * Get multiple documents by their IDs.
     * Returns latest version of each.
     * 
     * @param ids - Array of document IDs
     * @returns Array of documents (null entries for not found)
     */
    async getMany(ids: string[]): Promise<(Types.Document | null)[]> {
        const results: (Types.Document | null)[] = [];
        for (const id of ids) {
            results.push(await this.get(id));
        }
        return results;
    }

    /**
     * Check if a document exists in offline storage.
     * 
     * @param id - Document ID
     * @returns true if document exists
     */
    async exists(id: string): Promise<boolean> {
        const doc = await this.store.getLatest(id);
        return doc !== null;
    }

    /**
     * Get all children of a parent document.
     * 
     * @param parentPath - Parent path (document's p field)
     * @returns Array of child documents
     * 
     * @example
     * ```typescript
     * // Get all children of document with id 'folder-1'
     * const children = await offline.Documents.getChildren('/root/folder-1');
     * ```
     */
    async getChildren(parentPath: string): Promise<Types.Document[]> {
        const stored = await this.store.getByParent(parentPath);
        return stored.map(s => this._parseDocument(s));
    }

    /**
     * Get all descendants of a root path (recursive).
     * 
     * @param rootPath - Root path to start from (empty string for all documents)
     * @returns Array of all descendant documents
     * 
     * @example
     * ```typescript
     * // Get entire offline document tree
     * const allDocs = await offline.Documents.getDescendants('');
     * 
     * // Get all docs under a specific folder
     * const folderDocs = await offline.Documents.getDescendants('/root/folder-1');
     * ```
     */
    async getDescendants(rootPath: string = ''): Promise<Types.Document[]> {
        const stored = await this.store.getAllDescendants(rootPath);
        return stored.map(s => this._parseDocument(s));
    }

    /**
     * Find documents by form reference.
     * 
     * @param formId - Form ID to search for
     * @param formVersion - Optional form version
     * @returns Array of matching documents
     * 
     * @example
     * ```typescript
     * // Find all documents using a specific form
     * const invoices = await offline.Documents.findByForm('invoice-form');
     * ```
     */
    async findByForm(formId: string, formVersion?: string): Promise<Types.Document[]> {
        const stored = await this.store.findByForeign(
            formId,
            formVersion ? this._versionToNumber(formVersion) : undefined
        );
        return stored.map(s => this._parseDocument(s));
    }

    /**
     * Get total count of documents in offline storage.
     */
    async count(): Promise<number> {
        const all = await this.store.getAllDescendants('');
        return all.length;
    }

    /**
     * Get all stored document IDs.
     */
    async getAllIds(): Promise<string[]> {
        const all = await this.store.getAllDescendants('');
        return [...new Set(all.map(s => s.id))];
    }

    // ========================================================================
    // Sync Helpers
    // ========================================================================

    /**
     * Check if a document needs to be updated based on version.
     * 
     * @param id - Document ID
     * @param serverVersion - Version from server
     * @returns true if local version is older or missing
     */
    async needsUpdate(id: string, serverVersion: string): Promise<boolean> {
        const local = await this.store.getLatest(id);
        if (!local) {
            return true;
        }
        return local.v < this._versionToNumber(serverVersion);
    }

    // ========================================================================
    // Private Helpers
    // ========================================================================

    private _parseDocument(stored: StoredDocument): Types.Document {
        return JSON.parse(stored.json) as Types.Document;
    }

    /**
     * Convert version string to numeric version for storage/comparison.
     * Handles 'initial' as 0, numeric strings, and incrementing integers.
     */
    private _versionToNumber(v: string | number): number {
        if (typeof v === 'number') {
            return v;
        }
        if (v === 'initial') {
            return 0;
        }
        // Try to parse as number
        const num = parseInt(v, 10);
        if (!isNaN(num)) {
            return num;
        }
        // For UUIDs or other string versions, hash to a number
        // This is a simple hash - in practice you might want something more robust
        let hash = 0;
        for (let i = 0; i < v.length; i++) {
            const char = v.charCodeAt(i);
            hash = ((hash << 5) - hash) + char;
            hash = hash & hash; // Convert to 32bit integer
        }
        return Math.abs(hash);
    }
}
